import { useEffect, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { NJButton, NJIcon, NJIconButton, NJText } from '@engie-group/fluid-design-system-react';
import { PrincipalAvatar } from './parts';
import { findPrincipal } from './permissions';
import type { AccessRequest, Principal, SharingState } from './types';

/** Fictitious mailbox derived from the person's name (« prenom.nom@engie.com »). */
export function mailOf(principal: Principal): string {
    const slug = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z]+/g, '-');
    return `${principal.name.split(' ').map(slug).join('.')}@engie.com`;
}

const keyOf = (r: AccessRequest) => `${r.userId}@${r.requestedAt}`;
const when = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * The shared Nemo header renders the notification-centre icon (`more_time`) without any hook.
 * This attaches the prototype's notification centre to that button from the outside, without modifying the shell.
 */
function useHeaderTrigger(rootRef: RefObject<HTMLElement | null>, onToggle: () => void) {
    const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null);
    useEffect(() => {
        const button = rootRef.current?.querySelector<HTMLButtonElement>('header button[aria-label="more time action"], header button[data-ns-notifications]');
        if (!button) return;
        button.setAttribute('aria-label', 'Notifications');
        button.setAttribute('title', 'Notifications');
        button.dataset.nsNotifications = '';
        button.classList.add('ns-notif-trigger');
        button.addEventListener('click', onToggle);
        setTrigger(button);
        return () => button.removeEventListener('click', onToggle);
    }, [rootRef, onToggle]);
    return trigger;
}

/** The shared header hard-codes the « Study » badge; the prototype relabels it « Size2Mix » without modifying the shell. */
function useProductBadge(rootRef: RefObject<HTMLElement | null>) {
    useEffect(() => {
        const span = [...(rootRef.current?.querySelectorAll<HTMLSpanElement>('header span') ?? [])].find((s) => s.textContent === 'Study');
        if (!span) return;
        span.textContent = 'Size2Mix';
        span.parentElement?.classList.add('ns-product-badge');
    });
}

export function NotificationCenter({ rootRef, state, isOwner, onManage, networkName }: {
    networkName: string;
    rootRef: RefObject<HTMLElement | null>;
    state: SharingState;
    /** Only the owner receives access requests. */
    isOwner: boolean;
    onManage: () => void;
}) {
    const [open, setOpen] = useState(false);
    const [seen, setSeen] = useState<Set<string>>(() => new Set());
    const [mail, setMail] = useState<AccessRequest | null>(null);
    const [toggle] = useState(() => () => setOpen((o) => !o));
    const trigger = useHeaderTrigger(rootRef, toggle);
    useProductBadge(rootRef);

    const items = isOwner ? [...state.requests].sort((a, b) => b.requestedAt.localeCompare(a.requestedAt)) : [];
    const unread = items.filter((r) => !seen.has(keyOf(r))).length;
    const owner = findPrincipal(state, state.ownerId);

    useEffect(() => {
        trigger?.setAttribute('aria-expanded', String(open));
        trigger?.setAttribute('aria-label', unread ? `Notifications, ${unread} non lue${unread > 1 ? 's' : ''}` : 'Notifications');
    }, [trigger, open, unread]);

    useEffect(() => {
        if (!open) return;
        const close = (e: Event) => {
            if (e instanceof KeyboardEvent ? e.key === 'Escape' : !(e.target as Element).closest('.ns-notif, .ns-notif-trigger')) setOpen(false);
        };
        document.addEventListener('keydown', close);
        document.addEventListener('mousedown', close);
        return () => {
            document.removeEventListener('keydown', close);
            document.removeEventListener('mousedown', close);
        };
    }, [open]);

    const markAllRead = () => setSeen(new Set(items.map(keyOf)));
    const rect = trigger?.getBoundingClientRect();

    return (
        <>
            {trigger && unread > 0 && createPortal(<span className="ns-notif-badge" aria-hidden>{unread}</span>, trigger)}
            {open && rect && createPortal(
                <div className="ns-surface ns-notif" role="dialog" aria-label="Notifications" style={{ top: rect.bottom + 4, right: Math.max(8, window.innerWidth - rect.right) }}>
                    <div className="ns-notif__head">
                        <NJText scale="lg" className="ns-strong ns-grow">Notifications</NJText>
                        {unread > 0 && <NJButton label="Tout marquer comme lu" variant="secondary" emphasis="minimal" scale="sm" onClick={markAllRead} />}
                    </div>
                    {items.length === 0 ? (
                        <div className="ns-notif__empty">
                            <NJIcon name="notifications_none" />
                            <NJText scale="sm" variant="secondary">Aucune notification</NJText>
                        </div>
                    ) : (
                        <ul className="ns-notif__list">
                            {items.map((r) => {
                                const user = findPrincipal(state, r.userId);
                                if (!user) return null;
                                const isNew = !seen.has(keyOf(r));
                                const read = () => setSeen((s) => new Set(s).add(keyOf(r)));
                                return (
                                    <li key={keyOf(r)} className={`ns-notif__item${isNew ? ' ns-notif__item--new' : ''}`}>
                                        <PrincipalAvatar principal={user} />
                                        <div className="ns-notif__body">
                                            <NJText scale="sm"><strong>{user.name}</strong> demande l’accès au réseau <strong>{networkName}</strong>{r.page ? `, depuis la page ${r.page}` : ''}.</NJText>
                                            <NJText scale="xs" variant="secondary">{when(r.requestedAt)} · Envoyée aussi par e-mail</NJText>
                                            <div className="ns-notif__actions">
                                                <NJButton label="Gérer la demande" scale="sm" emphasis="subtle" onClick={() => { read(); setOpen(false); onManage(); }} />
                                                <NJButton label="Voir l’e-mail" variant="secondary" emphasis="minimal" scale="sm" icon="mail" onClick={() => { read(); setOpen(false); setMail(r); }} />
                                            </div>
                                        </div>
                                        {isNew && <span className="ns-notif__dot" aria-label="Non lue" />}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>,
                document.body,
            )}
            {mail && owner && <MailPreview networkName={networkName} state={state} request={mail} owner={owner} onClose={() => setMail(null)} onManage={() => { setMail(null); onManage(); }} />}
        </>
    );
}

/** Preview of the e-mail sent to the owner (prototype only: nothing is sent). */
function MailPreview({ networkName, state, request, owner, onClose, onManage }: { networkName: string; state: SharingState; request: AccessRequest; owner: Principal; onClose: () => void; onManage: () => void }) {
    const user = findPrincipal(state, request.userId);
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);
    if (!user) return null;
    const firstName = owner.name.split(' ')[0];

    return createPortal(
        <div className="ns-scrim" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="ns-surface ns-modal ns-mail" role="dialog" aria-modal="true" aria-label="E-mail envoyé à la propriétaire">
                <div className="ns-notif__head">
                    <NJIcon name="mail" />
                    <NJText scale="lg" className="ns-strong ns-grow">E-mail envoyé</NJText>
                    <NJIconButton icon="close" label="Fermer" onClick={onClose} />
                </div>
                <dl className="ns-mail__meta">
                    <dt>De</dt><dd>Nemo &lt;no-reply@nemo.engie.com&gt;</dd>
                    <dt>À</dt><dd>{owner.name} &lt;{mailOf(owner)}&gt;</dd>
                    <dt>Objet</dt><dd><strong>{user.name} demande l’accès au réseau « {networkName} »</strong></dd>
                </dl>
                <div className="ns-mail__body">
                    <NJText>Bonjour {firstName},</NJText>
                    <NJText>{user.name} ({mailOf(user)}) demande l’accès au réseau <strong>{networkName}</strong> dont vous êtes propriétaire{request.page ? `, depuis la page ${request.page}` : ''}.</NJText>
                    <NJText>Vous pouvez lui donner le droit de consulter ou de modifier le réseau, ou refuser la demande.</NJText>
                    <div><NJButton label="Gérer les accès" onClick={onManage} /></div>
                    <NJText scale="xs" variant="secondary">Demande reçue le {when(request.requestedAt)}. Vous recevez cet e-mail car vous êtes propriétaire du réseau dans Nemo.</NJText>
                </div>
            </div>
        </div>,
        document.body,
    );
}
